/** @jsxImportSource @emotion/react */
'use memo'

import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'

/**
 * One registration card's skeleton — the identity row, the action strip and
 * the divisions toggle, at the real card's sizes, so a card landing in its
 * place doesn't move anything. Used for the cold load and for the next page
 * arriving at the end of the list.
 */
const OrgRegistrationCardSkeleton = () => {
  'use memo'

  return (
    <Card aria-hidden='true'>
      <Identity>
        <IdentityMain>
          <Bar barWidth='72px' barHeight='72px' shrink />
          <Stack>
            <Row>
              <Bar barWidth='112px' barHeight='22px' pill />
              <Bar barWidth='88px' barHeight='22px' pill />
            </Row>
            <Bar barWidth='320px' barHeight='28px' />
            <Bar barWidth='140px' barHeight='14px' />
          </Stack>
        </IdentityMain>
        <Row>
          <Bar barWidth='200px' barHeight='48px' />
          <Bar barWidth='180px' barHeight='48px' />
        </Row>
      </Identity>
      <Strip>
        <Bar barWidth='160px' barHeight='56px' />
        <Bar barWidth='100%' barHeight='6px' grow />
        <Bar barWidth='240px' barHeight='40px' />
      </Strip>
      <Toggle>
        <Bar barWidth='180px' barHeight='14px' />
        <Bar barWidth='32px' barHeight='32px' />
      </Toggle>
    </Card>
  )
}

export default OrgRegistrationCardSkeleton

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

const Card = styled.div`
  background: ${colors.SECONDARY_350};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const Identity = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 32px;
  padding: 24px;
`

const IdentityMain = styled.div`
  flex: 999 1 240px;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
`

const Stack = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 32px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_400};
`

const Toggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 16px 0 24px;
  border-top: 1px solid ${colors.SECONDARY_200};
`

const Bar = styled.div<{
  barWidth: string
  barHeight: string
  pill?: boolean
  shrink?: boolean
  grow?: boolean
}>`
  width: ${({ barWidth }) => barWidth};
  max-width: 100%;
  height: ${({ barHeight }) => barHeight};
  flex: ${({ shrink, grow }) =>
    grow ? '1 1 220px' : shrink ? '0 0 auto' : '0 1 auto'};
  border-radius: ${({ pill }) => (pill ? '999px' : '8px')};
  background-color: ${colors.SECONDARY_200};
  animation: ${pulse} 1.4s ease-in-out infinite;
`
